import { all, call, put, takeLatest, select } from 'redux-saga/effects'
import * as Sentry from '@sentry/react'
import axios from 'axios'
import Cookies from 'js-cookie'
import {
    CREATE_SMB_ORDER_REQUEST,
    CREATE_SMB_PAYMENT_INTENT_REQUEST,
    createSmbOrderRequest,
    createSmbOrderResponse,
    GET_SMB_CARDS_REQUEST,
    GET_SMB_PAYMENT_METHOD_REQUEST,
    getSmbCardsResponse,
    getSmbPaymentMethodResponse,
    orderCancelled,
    paymentRequiresAction,
    UPDATE_SMB_PAYMENT_METHOD_REQUEST,
    updateSmbPaymentMethodResponse,
    SMB_LOGIN_REQUEST,
    SMB_REGISTRATION_REQUEST,
    smbLoginResponse,
    smbRegistrationResponse,
    SMB_GET_CART_CONTENTS_REQUEST,
    getSmbCartContentsResponse,
    GET_SMB_VIRTUAL_GIFT_TEMPLATES_REQUEST,
    getSmbVirtualGiftTemplatesResponse,
    SMB_ADD_TO_CART,
    SMB_REMOVE_FROM_CART,
    setSmbCartFlyoutMode,
    smbItemsSyncedToBackend,
    SMB_ITEMS_SYNCED_TO_BACKEND,
    smbCheckLoginResponse,
    SMB_CHECK_LOGIN_REQUEST,
    SMB_UPDATE_DELIVERY_GROUP,
    resetSmbCart,
    getSmbCartContentsRequest,
    createSmbPaymentIntentResponse,
    smbEditCartItemResponse,
    SMB_EDIT_CART_ITEM_REQUEST,
    SMB_SEND_COBRAND_FOR_APPROVAL_REQUEST,
    smbSendCobrandForApprovalResponse,
    smbSetCobrandBuilderStep,
    smbGetUserCobrandsResponse,
    SMB_GET_USER_COBRANDS_REQUEST,
} from '../actions/smbCart'
import { getSMBStatus } from '../actions/buyBulk'
import isUndefined from 'lodash/isUndefined'
import has from 'lodash/has'
import { sendAddToCartItemsToDataLayer } from '../utils/dataLayer'
import { SMB_APPLICATION_SOURCE } from '../constants'

const axiosInstance = axios.create({
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
})

function* getCards(action) {
    const includePlastic = Number(action.payload.includePlastic)
    const includeVirtual = Number(action.payload.includeVirtual)
    const cardNetwork = action.payload.cardNetwork

    try {
        const { data } = yield call(axiosInstance.request, {
            url: '/ajax/open-loop-products/',
            contentType: 'application/json',
            params: {
                include_plastic_cards: includePlastic,
                include_virtual_cards: includeVirtual,
                card_network: cardNetwork,
                smb: 1,
            },
        })

        yield put(getSmbCardsResponse({ data }))
    } catch (e) {
        yield call(Sentry.captureException, e, {
            contexts: {
                data: {
                    includePlastic: JSON.stringify(
                        action.payload.includePlastic
                    ),
                    includeVirtual: JSON.stringify(
                        action.payload.includeVirtual
                    ),
                    cardNetwork: JSON.stringify(action.payload.cardNetwork),
                },
            },
        })
        yield put(getSmbCardsResponse(e.response, '', true))
    }
}

function* getVirtualGiftTemplates(action) {
    try {
        const { data } = yield call(axiosInstance.request, {
            url: `/ajax/gift-templates/?page=${action.payload.page}&limit=${action.payload.limit}&smb=1`,
        })
        yield put(getSmbVirtualGiftTemplatesResponse(data))
    } catch (e) {
        yield call(Sentry.captureException, e, {
            contexts: {
                data: {
                    payload: JSON.stringify(action.payload),
                },
            },
        })
        yield put(getSmbVirtualGiftTemplatesResponse(e.response, '', true))
    }
}

function* getPaymentMethods(action) {
    try {
        const { data } = yield call(axiosInstance.request, {
            url: '/ajax/payment-methods/',
        })

        yield put(getSmbPaymentMethodResponse({ data }))
    } catch (e) {
        yield call(Sentry.captureException, e)
        yield put(getSmbPaymentMethodResponse(e.response, '', true))
    }
}

function* updatePaymentMethod(action) {
    const defaultErrorMsg =
        'Error processing your payment info. Please verify and try again.'
    try {
        const { data } = yield call(axiosInstance.request, {
            url: '/ajax/account/payment/',
            method: 'post',
            data: {
                stripeToken: action.payload.paymentMethod.paymentMethod.id,
                save_payment: action.payload.save,
            },
        })

        if (data.success === undefined || data.success !== 1) {
            yield put(
                updateSmbPaymentMethodResponse(
                    data.error || defaultErrorMsg,
                    '',
                    true
                )
            )
        } else {
            yield put(updateSmbPaymentMethodResponse(data.data))
        }
    } catch (e) {
        yield call(Sentry.captureException, e, {
            contexts: {
                data: {
                    payload: JSON.stringify(action.payload),
                },
            },
        })
        yield put(
            updateSmbPaymentMethodResponse(
                e.response.data.error || defaultErrorMsg,
                '',
                true
            )
        )
    }
}

function* createPaymentIntent(action) {
    const defaultErrorMsg =
        'Error processing your payment info. Please verify and try again.'
    // Get checkout payload to server
    const createOrderPayload = yield select((state) => {
        return {
            user_payment_method_id: state.smbCart.checkoutPaymentMethod.id,
        }
    })

    // Must specify smb so that the correct cart is used for checkout
    action.payload['smb'] = 1

    try {
        const { data } = yield call(
            axiosInstance.post,
            `/ajax/cart/payment-intent/`,
            action.payload
        )

        if (data && data.requires_action) {
            // Further action is required by the client to validate the Credit card payment
            yield put(paymentRequiresAction(data.payment_intent_client_secret))
        } else {
            yield put(createSmbOrderRequest(createOrderPayload))
        }
    } catch (error) {
        yield call(Sentry.captureException, error, {
            contexts: {
                data: {
                    payload: JSON.stringify(action.payload),
                },
            },
            user: {
                id: Cookies.get('GCG_USER_ID'),
            },
        })
        yield put(
            createSmbPaymentIntentResponse(
                error.response.data.error || defaultErrorMsg,
                {},
                true
            )
        )
    }
}

function* createOrder(action) {
    try {
        const { data } = yield call(
            axiosInstance.post,
            `/ajax/create-smb-order/`,
            action.payload
        )
        yield put(
            createSmbOrderResponse({
                order: data.order,
                cards: data.order_cards,
            })
        )
        yield put(resetSmbCart())
    } catch (e) {
        // a variety of responses here: errors or error_code
        const errorData = {
            errors: (has(e.response, 'data') && e.response.data.errors) || [],
            error_code:
                (has(e.response, 'data') && e.response.data.error_code) || null,
        }

        if (errorData.error_code === 'order_cancelled') {
            yield put(
                orderCancelled(
                    {
                        order: e.response.data.order,
                        cards: e.response.data.order_cards,
                        cancelReason: e.response.data.cancel_reason,
                    },
                    {},
                    true
                )
            )
            yield put(resetSmbCart())
            return
        }
        yield call(Sentry.captureException, errorData, {
            contexts: {
                data: {
                    order: JSON.stringify(action.payload),
                },
            },
            user: {
                id: Cookies.get('GCG_USER_ID'),
            },
        })
        yield put(getSmbCartContentsRequest())
        yield put(createSmbOrderResponse(errorData, {}, true))
    }
}

function* handleAddToCart(action) {
    const { meta, error } = action
    if (error || (meta && meta.sent === true)) {
        return
    }

    const payload = yield select((state) => state.smbCart.itemsPayload)

    if (payload.cards.length > 0 && payload.delivery_groups.length > 0) {
        try {
            const { data } = yield call(
                axiosInstance.post,
                `/ajax/cart/`,
                payload
            )
            // Push add to cart data layer event
            sendAddToCartItemsToDataLayer(
                data.cart.cart_items,
                payload.delivery_groups[0].uuid
            )
            yield put(smbItemsSyncedToBackend(data, { change: true }))
        } catch (error) {
            yield call(Sentry.captureException, error, {
                user: {
                    id: Cookies.get('GCG_USER_ID'),
                },
                extra: { GCG_SMB_CART: Cookies.get('GCG_SMB_CART') },
                contexts: {
                    data: {
                        delivery_groups: JSON.stringify(
                            payload.delivery_groups
                        ),
                        cards: JSON.stringify(payload.cards),
                    },
                },
            })
            yield put(smbItemsSyncedToBackend(error.response.data, {}, true))
        }
    }
}

function* handleRemoveFromCart(action) {
    if (
        has(action.payload, 'cart_id') &&
        has(action.payload, 'delivery_group_uuid')
    ) {
        let data
        try {
            const response = yield call(
                axiosInstance.post,
                `/ajax/cart/remove-delivery-group/`,
                action.payload
            )
            data = response.data
            yield put(smbItemsSyncedToBackend(data, { removingFromCart: true }))
        } catch (error) {
            yield call(Sentry.captureException, error, {
                contexts: {
                    data: {
                        cart_id: JSON.stringify(action.payload.cart_id),
                        delivery_group_uuid: JSON.stringify(
                            action.payload.delivery_group_uuid
                        ),
                    },
                },
            })
            yield put(smbItemsSyncedToBackend(error.response.data, {}, true))
        }
    }
}

function* handleEditCart(action) {
    const payload = yield select((state) => state.smbCart.itemsPayload)

    if (payload.cards.length > 0 && payload.delivery_groups.length > 0) {
        try {
            // Delete cart items first because adding replacement items
            // first could exceed the order limit.
            yield call(axiosInstance.request, {
                method: 'POST',
                url: '/ajax/cart/remove-delivery-group/',
                data: {
                    cart_id: action.payload.cart_id,
                    delivery_group_uuid: action.payload.delivery_group_uuid,
                },
            })
            // Add updated items to cart.
            const { data } = yield call(axios.post, `/ajax/cart/`, payload)

            yield put(smbItemsSyncedToBackend(data, { change: true }))
            yield put(smbEditCartItemResponse())
        } catch (error) {
            yield call(Sentry.captureException, error, {
                contexts: {
                    data: {
                        cart_id: JSON.stringify(action.payload.cart_id),
                        delivery_group_uuid: JSON.stringify(
                            action.payload.delivery_group_uuid
                        ),
                    },
                },
            })
            yield put(smbEditCartItemResponse(error, null, true))
        }
    }
}

function* handleDeliveryGroupUpdate(action) {
    if (
        has(action.payload, 'cart_id') &&
        has(action.payload, 'delivery_group_uuid') &&
        has(action.payload, 'shipping_method_id')
    ) {
        let data
        try {
            const response = yield call(
                axiosInstance.post,
                `/ajax/cart/update-delivery-group/`,
                action.payload
            )
            data = response.data
            yield put(smbItemsSyncedToBackend(data, { change: true }))
        } catch (error) {
            yield call(Sentry.captureException, error, {
                contexts: {
                    data: {
                        cart_id: JSON.stringify(action.payload.cart_id),
                        delivery_group_uuid: JSON.stringify(
                            action.payload.delivery_group_uuid
                        ),
                    },
                },
            })
            yield put(smbItemsSyncedToBackend(error.response.data, {}, true))
        }
    }
}

function* handleItemsSyncedToBackend(action) {
    yield put(setSmbCartFlyoutMode({ addingToCart: false, addedToCart: true }))
    return action.payload
}

function* getCartContents(action) {
    try {
        const { data } = yield call(axiosInstance.request, {
            url: '/ajax/cart/',
            params: {
                smb: 1,
            },
        })

        yield put(getSmbCartContentsResponse(data))
    } catch (e) {
        yield call(Sentry.captureException, e)
        yield put(getSmbCartContentsResponse(e.response.data, '', true))
    }
}

function* smbLogin(action) {
    const { email, password, recaptchaToken } = action.payload

    try {
        const res = yield call(axiosInstance.request, {
            url: '/ajax/user-login/',
            method: 'POST',
            data: {
                email: email,
                password: password,
                recaptchaToken: recaptchaToken,
            },
        })

        if (res.status !== 200) {
            yield put(smbLoginResponse('Login unsuccessful', '', true))
            return
        }
        yield put(smbLoginResponse())
        yield call(checkLogin)
    } catch (e) {
        yield call(Sentry.captureException, e)
        yield put(smbLoginResponse(e.response, '', true))
    }
}

function* smbRegistration(action) {
    let postData = {
        company_name: action.payload.companyName,
        company_phone_number: action.payload.companyPhoneNumber,
        address_line1: action.payload.addressLine1,
        address_line2: action.payload.addressLine2,
        city: action.payload.city,
        state: action.payload.stateAbbr,
        zip_code: action.payload.zipCode,
        contact_name: action.payload.contactName,
        contact_phone_number: action.payload.contactNumber,
        contact_email: action.payload.contactEmail,
        website: action.payload.website,
        password: action.payload.password,
        confirmed_password: action.payload.confirmedPassword,
        ein: action.payload.ein,
        ssn: action.payload.ssn,
        reason_for_interest: action.payload.reasonForInterest,
        recaptchaToken: action.payload.recaptchaToken,
        source: SMB_APPLICATION_SOURCE.ORDER_FLOW,
    }

    // Remove keys that don't have a value
    Object.keys(postData).forEach(
        (k) => !postData[k] && postData[k] !== undefined && delete postData[k]
    )

    try {
        const response = yield call(axiosInstance.request, {
            url: '/ajax/submit-small-business-registration/',
            method: 'POST',
            data: postData,
        })

        yield put(smbRegistrationResponse(response.data))
        yield call(checkLogin)
    } catch (e) {
        yield call(Sentry.captureException, e)
        yield put(smbRegistrationResponse(e.response, '', true))
    }
}

function* checkLogin(action) {
    let saveUserInfo = false
    if (!isUndefined(action) && !isUndefined(action.payload)) {
        saveUserInfo = action.payload.saveUserInfo
    }

    try {
        const res = yield call(axiosInstance.request, {
            url: '/ajax/check-login/',
            method: 'GET',
        })
        const loggedIn = res.data.is_logged_in
        const user = {
            name: res.data.full_name,
            email: res.data.username,
            phoneNumber: res.data.phone_number,
        }
        yield put(
            smbCheckLoginResponse({
                isLoggedIn: loggedIn,
                user: saveUserInfo ? user : null,
            })
        )
        const { smb_status } = yield select(({ buyBulk }) => buyBulk.smbStatus)
        if (loggedIn && smb_status === null) {
            yield put(getSMBStatus())
        }
    } catch (e) {
        yield call(Sentry.captureException, e)
        yield put(smbCheckLoginResponse({ isLoggedIn: false, user: null }))
    }
}

function* handleSendCobrandForApproval(action) {
    const { meta, error } = action
    if (error || (meta && meta.sent === true)) {
        return
    }

    const {
        cobrandId,
        name,
        companyName,
        purpose,
        audience,
        cloudinaryId,
    } = yield select((state) => state.smbCart.cobrandCard)

    if (
        cobrandId &&
        name &&
        companyName &&
        purpose &&
        audience &&
        cloudinaryId
    ) {
        let data
        try {
            const response = yield call(
                axiosInstance.post,
                `/ajax/submit-cobrand/`,
                {
                    uuid: cobrandId,
                    name: name,
                    company_name: companyName,
                    purpose_of_program: purpose,
                    primary_audience: audience,
                    cloudinary_id: cloudinaryId,
                }
            )
            if (response.status !== 201) {
                yield put(
                    smbSendCobrandForApprovalResponse(
                        'Cobrand submission unsuccessful',
                        '',
                        true
                    )
                )
                return
            }
            data = response.data
            yield put(smbSendCobrandForApprovalResponse(data))
            yield put(smbSetCobrandBuilderStep(3))
        } catch (error) {
            console.log('Cobrand submission error: ', error)
            yield call(Sentry.captureException, error, {
                user: {
                    id: Cookies.get('GCG_USER_ID'),
                },
                contexts: {
                    data: {
                        cobrand_card_payload: JSON.stringify({
                            uuid: cobrandId,
                            cloudinary_id: cloudinaryId,
                            company_name: companyName,
                            purpose_of_program: purpose,
                            primary_audience: audience,
                        }),
                    },
                },
            })
            yield put(
                smbSendCobrandForApprovalResponse(error.response, '', true)
            )
        }
    }
}

function* getUserCobrands() {
    try {
        const { data } = yield call(axiosInstance.request, {
            url: '/ajax/user-cobrands/',
            contentType: 'application/json',
        })
        if (!data) {
            yield put(
                smbGetUserCobrandsResponse('No user cobrands found', '', true)
            )
            return
        }
        yield put(smbGetUserCobrandsResponse({ data }))
    } catch (e) {
        yield call(Sentry.captureException, e)
        yield put(smbGetUserCobrandsResponse(e, '', true))
    }
}

export function* smbCartSagas() {
    yield all([
        takeLatest(SMB_ADD_TO_CART, handleAddToCart),
        takeLatest(SMB_REMOVE_FROM_CART, handleRemoveFromCart),
        takeLatest(SMB_ITEMS_SYNCED_TO_BACKEND, handleItemsSyncedToBackend),
        takeLatest(SMB_UPDATE_DELIVERY_GROUP, handleDeliveryGroupUpdate),
        takeLatest(GET_SMB_CARDS_REQUEST, getCards),
        takeLatest(
            GET_SMB_VIRTUAL_GIFT_TEMPLATES_REQUEST,
            getVirtualGiftTemplates
        ),
        takeLatest(GET_SMB_PAYMENT_METHOD_REQUEST, getPaymentMethods),
        takeLatest(UPDATE_SMB_PAYMENT_METHOD_REQUEST, updatePaymentMethod),
        takeLatest(CREATE_SMB_PAYMENT_INTENT_REQUEST, createPaymentIntent),
        takeLatest(CREATE_SMB_ORDER_REQUEST, createOrder),
        takeLatest(SMB_LOGIN_REQUEST, smbLogin),
        takeLatest(SMB_REGISTRATION_REQUEST, smbRegistration),
        takeLatest(SMB_GET_CART_CONTENTS_REQUEST, getCartContents),
        takeLatest(SMB_CHECK_LOGIN_REQUEST, checkLogin),
        takeLatest(SMB_EDIT_CART_ITEM_REQUEST, handleEditCart),
        takeLatest(
            SMB_SEND_COBRAND_FOR_APPROVAL_REQUEST,
            handleSendCobrandForApproval
        ),
        takeLatest(SMB_GET_USER_COBRANDS_REQUEST, getUserCobrands),
    ])
}

export default smbCartSagas
